import React from "react";
import { useParams } from "react-router-dom";
import { getOptionLabel, getSelectedTenantCurrencyCodes, getTenantOptionDisplayLabels } from "helpers/couponScopeOptions";
import routes from "constants/routes";
import {
  COUPON_PREVIEW_LIST_SCROLL_THRESHOLD,
  COUPON_PREVIEW_NOT_SET,
  COUPON_STATUS,
  ZERO,
} from "constants/coupons";
import { getCurrencyLabel } from "helpers/couponDiscountType";
import {
  getDiscountDisplay,
  getPreviewSubtitle,
  getPreviewText,
  isEmptyPreviewValue,
  getPreviewCreatedOnDisplay,
  hasPreviewCreatedOn,
  STATUS_BADGE_CLASS_SUFFIX,
} from "helpers/couponFormDisplay";
import { getCouponCategoryLabel } from "utils/couponUtils";
import { filterItemsBySearchTerm } from "utils/searchUtils";
import { Icon, SearchBar } from "widgets";

import FormSection from "../FormSection";
import styles from "../CouponForm.module.scss";

const STATUS_BADGE_CLASS = {
  [COUPON_STATUS.ACTIVE]: styles[STATUS_BADGE_CLASS_SUFFIX.ACTIVE],
  [COUPON_STATUS.INACTIVE]: styles[STATUS_BADGE_CLASS_SUFFIX.INACTIVE],
  [COUPON_STATUS.EXPIRED]: styles[STATUS_BADGE_CLASS_SUFFIX.EXPIRED],
  [COUPON_STATUS.SCHEDULED]: styles[STATUS_BADGE_CLASS_SUFFIX.SCHEDULED],
};

const CouponFormSidebar = ({ form }) => {
  const { id: routeCouponCode } = useParams();
  const {
    isView,
    isCreate,
    values,
    codeCopied,
    handleCopyCode,
    isSectionCollapsed,
    toggleSectionCollapse,
    computedStatus,
    statusLabel,
    previewPackageSearch,
    setPreviewPackageSearch,
    previewPackageData,
    previewPlatformItems,
    getValidityPreviewItems,
    getDiscountPreviewItems,
    getUsagePreviewItems,
    handleSubmit,
    isSubmitting,
    submitLabel,
    isFormSubmittable,
    navigate,
    verticalOptions,
    tenantOptions,
    currencyOptions,
    selectedCurrencyIds,
  } = form;

  const renderPreviewValueSpan = (value) => {
    const text = getPreviewText(value);

    return (
      <span
        className={`${styles.summaryValue} ${
          text === COUPON_PREVIEW_NOT_SET ? styles.summaryValueNotSet : ""
        }`}
      >
        {text}
      </span>
    );
  };

  const renderPreviewCategoryValue = () => {
    const text = values.category
      ? getCouponCategoryLabel(values.category)
      : COUPON_PREVIEW_NOT_SET;

    return (
      <span
        className={`${styles.summaryValue} ${
          text === COUPON_PREVIEW_NOT_SET ? styles.summaryValueNotSet : ""
        }`}
      >
        {text}
      </span>
    );
  };

  const renderSummaryBulletList = (
    items,
    { scrollable = false, packageList = false } = {}
  ) => {
    if (!items.length) {
      return (
        <span
          className={`${styles.summaryValue} ${styles.summaryValueNotSet}`}
        >
          {COUPON_PREVIEW_NOT_SET}
        </span>
      );
    }

    const useScroll =
      scrollable || items.length > COUPON_PREVIEW_LIST_SCROLL_THRESHOLD;
    const scrollClass = packageList
      ? styles.summaryPackageListScroll
      : styles.summaryBulletListScroll;

    return (
      <ul
        className={`${styles.summaryBulletList} ${
          useScroll ? scrollClass : ""
        }`}
      >
        {items.map((item, index) => (
          <li key={`${item}-${index}`} className={styles.summaryBulletItem}>
            {item}
          </li>
        ))}
      </ul>
    );
  };

  const renderSummaryDetailList = (items) => (
    <div className={styles.summaryDetailList}>
      {items.map((item) => (
        <div key={item.label} className={styles.summaryDetailItem}>
          <span className={styles.summaryDetailKey}>{item.label}</span>
          <span
            className={`${styles.summaryDetailValue} ${
              item.value === COUPON_PREVIEW_NOT_SET ? styles.summaryValueNotSet : ""
            }`}
          >
            {item.value}
          </span>
        </div>
      ))}
    </div>
  );

  const renderPackagesPreview = () => {
    const { isAll, count, items } = previewPackageData;
    const showPackageSearch =
      items.length >= COUPON_PREVIEW_LIST_SCROLL_THRESHOLD;
    const filteredItems = filterItemsBySearchTerm(items, previewPackageSearch);

    if (!items.length) {
      if (isAll) {
        return (
          <div className={styles.summaryAllBadge}>
            <span className={styles.summaryValue}>All packages</span>
            {count ? (
              <span className={styles.summaryCountPill}>{count} total</span>
            ) : null}
          </div>
        );
      }
      return (
        <span
          className={`${styles.summaryValue} ${styles.summaryValueNotSet}`}
        >
          {COUPON_PREVIEW_NOT_SET}
        </span>
      );
    }

    return (
      <div className={styles.summaryCompactGroup}>
        <div className={styles.summaryAllBadge}>
          <span className={styles.summaryValue}>
            {isAll ? "All packages" : "Selected packages"}
          </span>
          <span className={styles.summaryCountPill}>
            {count ?? items.length} total
          </span>
        </div>
        {showPackageSearch ? (
          <div className={styles.previewSearchWrap}>
            <SearchBar
              value={previewPackageSearch}
              onChange={setPreviewPackageSearch}
              placeholder="Search packages..."
              className={styles.previewSearchInput}
            />
          </div>
        ) : null}
        {filteredItems.length ? (
          renderSummaryBulletList(filteredItems, {
            scrollable: true,
            packageList: true,
          })
        ) : (
          <p className={styles.previewSearchEmpty}>No packages match search.</p>
        )}
        {previewPackageSearch.trim() && filteredItems.length ? (
          <p className={styles.previewSearchHint}>
            Showing {filteredItems.length} of {items.length}
          </p>
        ) : null}
      </div>
    );
  };

  const renderCopyCodeButton = () => (
    <button
      type="button"
      className={styles.copyCodeBtn}
      onClick={handleCopyCode}
      aria-label={codeCopied ? "Coupon code copied" : "Copy coupon code"}
      title={codeCopied ? "Copied!" : "Copy coupon code"}
    >
      <Icon
        name={codeCopied ? "check" : "contentCopy"}
        fontSize="small"
      />
    </button>
  );

  return (
    <aside className={styles.sidebar}>
      <div id="coupon-preview-panel" className={styles.previewPanel}>
        <FormSection
          sectionKey="previewTicket"
          title="Coupon Preview"
          variant="sidebar"
          isCollapsed={isSectionCollapsed("previewTicket")}
          onToggle={toggleSectionCollapse}
          className={`${styles.sidebarSectionFlush} ${styles.sidebarSectionCard}`}
        >
          <div className={styles.couponPreview}>
            <div className={styles.previewTop}>
              <div className={styles.previewCodeRow}>
                <span
                  className={
                    isEmptyPreviewValue(values.code)
                      ? `${styles.previewCode} ${styles.previewCodeNotSet}`
                      : styles.previewCode
                  }
                >
                  {getPreviewText(values.code)}
                </span>
                {values.code ? renderCopyCodeButton() : null}
              </div>
              {values.code && codeCopied && (
                <span className={styles.previewCopiedHint}>Copied!</span>
              )}
            </div>
            <div className={styles.previewBottom}>
              <p
                className={`${styles.previewDiscount} ${
                  getDiscountDisplay(values, currencyOptions) ===
                  COUPON_PREVIEW_NOT_SET
                    ? styles.summaryValueNotSet
                    : ""
                }`}
              >
                {getDiscountDisplay(values, currencyOptions)}
              </p>
              <p
                className={`${styles.previewDesc} ${
                  getPreviewSubtitle(values) === COUPON_PREVIEW_NOT_SET
                    ? styles.summaryValueNotSet
                    : ""
                }`}
              >
                {getPreviewSubtitle(values)}
              </p>
            </div>
          </div>
        </FormSection>

        <div className={styles.previewScrollArea}>
          <FormSection
            sectionKey="previewSummary"
            title="Coupon Summary"
            variant="sidebar"
            isCollapsed={isSectionCollapsed("previewSummary")}
            onToggle={toggleSectionCollapse}
            className={styles.sidebarSectionPreview}
          >
            <div className={styles.summaryList}>
              <div className={styles.summaryRow}>
                <Icon name="confirmationNumber" className={styles.summaryIcon} />
                <div className={styles.summaryContent}>
                  <span className={styles.summaryLabel}>Coupon Code</span>
                  {renderPreviewValueSpan(values.code)}
                </div>
              </div>
              {isCreate ? (
                <>
                  <div className={styles.summaryRow}>
                    <Icon name="filterList" className={styles.summaryIcon} />
                    <div className={styles.summaryContent}>
                      <span className={styles.summaryLabel}>Vertical</span>
                      {renderPreviewValueSpan(
                        getOptionLabel(verticalOptions, values.verticalId) ??
                          values.verticalId
                      )}
                    </div>
                  </div>
                  <div className={styles.summaryRow}>
                    <Icon name="barChart" className={styles.summaryIcon} />
                    <div className={styles.summaryContent}>
                      <span className={styles.summaryLabel}>Currency</span>
                      {renderPreviewValueSpan(
                        getSelectedTenantCurrencyCodes(
                          tenantOptions,
                          values.tenantIds,
                          currencyOptions
                        ).join(", ") ||
                          selectedCurrencyIds
                            .map((currencyId) =>
                              getCurrencyLabel(currencyOptions, currencyId)
                            )
                            .join(", ")
                      )}
                    </div>
                  </div>
                  <div className={styles.summaryRow}>
                    <Icon name="language" className={styles.summaryIcon} />
                    <div className={styles.summaryContent}>
                      <span className={styles.summaryLabel}>Tenant</span>
                      {renderPreviewValueSpan(
                        getTenantOptionDisplayLabels(tenantOptions, values.tenantIds, {
                          includeCurrencyCode: true,
                        }) ?? values.tenantIds?.join(", ")
                      )}
                    </div>
                  </div>
                </>
              ) : (
                <div className={styles.summaryRow}>
                  <Icon name="language" className={styles.summaryIcon} />
                  <div className={styles.summaryContent}>
                    <span className={styles.summaryLabel}>Tenants</span>
                    {renderPreviewValueSpan(
                      getTenantOptionDisplayLabels(tenantOptions, values.tenantIds, {
                        includeCurrencyCode: true,
                      }) ?? values.tenantIds?.join(", ")
                    )}
                  </div>
                </div>
              )}
              <div className={styles.summaryRow}>
                <Icon name="localOffer" className={styles.summaryIcon} />
                <div className={styles.summaryContent}>
                  <span className={styles.summaryLabel}>Category</span>
                  {renderPreviewCategoryValue()}
                </div>
              </div>
              <div className={styles.summaryRow}>
                <Icon name="info" className={styles.summaryIcon} />
                <div className={styles.summaryContent}>
                  <span className={styles.summaryLabel}>Remarks</span>
                  {renderPreviewValueSpan(values.remarks)}
                </div>
              </div>
              <div className={styles.summaryRow}>
                <Icon name="fiberManualRecord" className={styles.summaryIcon} />
                <div className={styles.summaryContent}>
                  <span className={styles.summaryLabel}>Status</span>
                  <span
                    className={`${styles.summaryStatusActive} ${
                      STATUS_BADGE_CLASS[computedStatus] ?? ""
                    }`}
                  >
                    {statusLabel}
                  </span>
                </div>
              </div>
              <div className={styles.summaryRow}>
                <Icon name="dateRange" className={styles.summaryIcon} />
                <div className={styles.summaryContent}>
                  <span className={styles.summaryLabel}>Validity</span>
                  {renderSummaryDetailList(getValidityPreviewItems())}
                </div>
              </div>
              <div className={styles.summaryRow}>
                <Icon name="localOffer" className={styles.summaryIcon} />
                <div className={styles.summaryContent}>
                  <span className={styles.summaryLabel}>Discount</span>
                  {renderSummaryDetailList(getDiscountPreviewItems())}
                </div>
              </div>
              <div className={styles.summaryRow}>
                <Icon name="barChart" className={styles.summaryIcon} />
                <div className={styles.summaryContent}>
                  <span className={styles.summaryLabel}>Usage</span>
                  {renderSummaryDetailList(getUsagePreviewItems())}
                </div>
              </div>
              <div
                className={`${styles.summaryRow} ${styles.summaryRowPackages}`}
              >
                <Icon name="inventory" className={styles.summaryIcon} />
                <div
                  className={`${styles.summaryContent} ${styles.summaryContentPackages}`}
                >
                  <span className={styles.summaryLabel}>
                    Applicable Packages
                    {!previewPackageData.isAll &&
                    previewPackageData.items.length > ZERO
                      ? ` (${previewPackageData.items.length})`
                      : ""}
                  </span>
                  {renderPackagesPreview()}
                </div>
              </div>
              <div
                className={`${styles.summaryRow} ${styles.summaryRowPlatforms}`}
              >
                <Icon name="devices" className={styles.summaryIcon} />
                <div className={styles.summaryContent}>
                  <span className={styles.summaryLabel}>
                    Applicable Platforms
                    {previewPlatformItems.length > ZERO
                      ? ` (${previewPlatformItems.length})`
                      : ""}
                  </span>
                  {renderSummaryBulletList(previewPlatformItems)}
                </div>
              </div>
              <div className={styles.summaryRow}>
                <Icon name="calendarToday" className={styles.summaryIcon} />
                <div className={styles.summaryContent}>
                  <span className={styles.summaryLabel}>Created On</span>
                  <span
                    className={`${styles.summaryValue} ${
                      hasPreviewCreatedOn(values, isCreate)
                        ? ""
                        : styles.summaryValueNotSet
                    }`}
                  >
                    {getPreviewCreatedOnDisplay(values, isCreate)}
                  </span>
                </div>
              </div>
            </div>
          </FormSection>

          {!isCreate && !isView && (
            <div className={styles.changesNote}>
              <Icon name="info" className={styles.infoBoxIcon} fontSize="small" />
              <span>Changes will reflect for new orders only.</span>
            </div>
          )}
        </div>

        <div className={styles.sidebarActions}>
          {isView ? (
            <button
              type="button"
              className={`${styles.updateCouponBtn} ${styles.updateCouponBtnFull}`}
              onClick={() => {
                const couponCode = String(
                  values.code ?? routeCouponCode ?? ""
                ).trim();
                navigate(routes.coupons.edit(couponCode));
              }}
            >
              Edit Coupon
            </button>
          ) : (
            <button
              type="button"
              className={`${styles.updateCouponBtn} ${styles.updateCouponBtnFull}`}
              onClick={handleSubmit}
              disabled={isSubmitting || !isFormSubmittable}
            >
              {isSubmitting ? "Saving..." : submitLabel}
            </button>
          )}
        </div>
      </div>
    </aside>
  );
};

export default CouponFormSidebar;
